import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Paper,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import type { DailyAuditSummary } from '../../types/services/storeAuditReports';
import type { Store as StoreType } from '../../types/services/auth';
import { formatDateMMDDYYYY } from '../../utils/formatUtils';
import LegacyReconciliationSummaryTiles from './LegacyReconciliationSummaryTiles';
import LegacyReconciliationExpandedReportTable from './LegacyReconciliationExpandedReportTable';
import { useDailyExpandedAuditReport } from '../../hooks/logic/useDailyExpandedAuditReport';

type LegacyReconciliationDialogProps = {
  open: boolean;
  onClose: () => void;
  summary: DailyAuditSummary | null;
  store?: StoreType | null;
};

const LegacyReconciliationDialog: React.FC<LegacyReconciliationDialogProps> = ({ open, onClose, summary, store }) => {
  const totals = summary?.daily_totals;
  const [resolutionAmount, setResolutionAmount] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const reportDate = useMemo(() => (
    summary?.report_date ? dayjs(summary.report_date).format('YYYY-MM-DD') : null
  ), [summary?.report_date]);

  const dailyExpandedState = useDailyExpandedAuditReport({
    enabled: Boolean(open),
    storeId: store?.id,
    reportDate: reportDate ?? undefined,
  });

  const handleResolve = () => {
    console.info('Resolve discrepancy', {
      amount: resolutionAmount,
      note: resolutionNote,
      summary,
    });
    onClose();
  };

  if (!summary) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle sx={{ pb: 1.5, background: '#EEE8FF', color: '#222B45', fontWeight: 'bold', mb: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '18px' }}>
              {store?.name || 'Store'} — Reconciliation
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '14px', fontStyle: 'italic' }}>
              {formatDateMMDDYYYY(summary.report_date)}
            </Typography>
          </Box>
          {/* <IconButton
            onClick={handleExport}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <Download />
          </IconButton> */}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'rgba(247,249,252,1)' }}>
        <Stack spacing={3} className="legacy-reconciliation-popup">
          <LegacyReconciliationSummaryTiles totals={totals} store={store} />

          <LegacyReconciliationExpandedReportTable
            report={dailyExpandedState.data}
            loading={dailyExpandedState.loading}
            error={dailyExpandedState.error}
            store={store}
          />

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Reconcile Discrepancy
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Resolution Amount"
                type="number"
                size="small"
                value={resolutionAmount}
                onChange={(e) => setResolutionAmount(e.target.value)}
              />
              <TextField
                label="Notes"
                size="small"
                multiline
                minRows={3}
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={onClose} sx={{ color: '#2C6CA0', borderColor: '#2C6CA0', fontWeight: 400, fontSize: '16px' }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleResolve} disabled={!resolutionAmount} sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', backgroundColor: '#2C6CA0' }}>
                  Mark as Resolved
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" 
        sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', backgroundColor: '#2C6CA0' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegacyReconciliationDialog;
